import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject, combineLatest } from 'rxjs';
import { first, map, switchMap, takeUntil } from 'rxjs/operators';

import {
  DeviceBus,
  DeviceRoles,
  Feature,
  FeatureNames,
  IsNumberValueProperty,
  IsStringValueProperty,
} from '@vi-iot/client';

import { NO_VALUE } from '@pipes';
import { UIService } from '@services';
import {
  DevicesStore,
  RichDevice,
  HemsStore,
  InstallationDetailsStore,
  isWallboxDevice,
  OwnerDetails,
  AuthStore,
  WallboxDevicesWithMatchingBoilerSerial,
  DashboardTypes,
  isSolarLogInverter,
  InstallationFeaturesStore,
} from '@store';

import { LiveUpdatesDataService } from '../../../shared/live-updates/live-updates-data.service';
import { LiveUpdatesService } from '../../../shared/live-updates/live-updates.service';
import { DragDropLayoutService } from '../drag-drop-layout/drag-drop-layout.service';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';

@Component({
  selector: 'app-installation-details-header',
  templateUrl: './installation-details-header.component.html',
  styleUrls: ['./installation-details-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationDetailsHeaderComponent implements OnInit, OnDestroy {
  readonly NO_VALUE = NO_VALUE;
  readonly DashboardTypes = DashboardTypes;
  FeaturesDetailsOverlayTypeEnum = FeaturesDetailsOverlayTypeEnum;
  standaloneLoading = true;
  owner: OwnerDetails;
  idOfOpenedWallboxDevice: string;
  heatingSpfTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  gatewaySerial: string;
  isAnyDeviceWithVitochargeRole: boolean;
  wallboxDevices$: Observable<WallboxDevicesWithMatchingBoilerSerial[]>;
  private currentlySelectedDevice: RichDevice;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public devicesStore: DevicesStore,
    public hemsStore: HemsStore,
    public uiService: UIService,
    public installationDetailsStore: InstallationDetailsStore,
    protected installationFeaturesStore: InstallationFeaturesStore,
    public authStore: AuthStore,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private liveUpdatesService: LiveUpdatesService,
    private liveUpdatesDataService: LiveUpdatesDataService,
    private dragAndDropService: DragDropLayoutService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.gatewaySerial = queryParams?.gatewaySerial;
    });
    this.heatingSpfTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingSpfTotal
    );
    this.devicesStore.mainDevicesForHeaderTiles$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((devices: RichDevice[]) => {
        if (!Array.isArray(devices)) {
          return;
        }
        this.isAnyDeviceWithVitochargeRole = devices.some((device: RichDevice) =>
          device.roles.includes(DeviceRoles.ProductVitocharge)
        );
      });
    this.devicesStore.selectedDevice$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((device) => {
      this.currentlySelectedDevice = device;
    });

    this.devicesStore.updateDevicesWithDiagnosticData();
    this.sortAllWallboxDevicesWithMatchingBoilerSerial();
  }

  getDeviceThirdpartyModelFeature(
    deviceId: string,
    gatewaySerial?: string
  ): Observable<Feature<IsStringValueProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceThirdpartyModel,
      deviceId,
      gatewaySerial
    );
  }

  getDeviceEtnFeature(
    deviceId: string,
    gatewaySerial?: string
  ): Observable<Feature<IsStringValueProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceEtn,
      deviceId,
      gatewaySerial
    );
  }

  openDryContactDevice(device: RichDevice): void {
    this.devicesStore.setSelectedDevice(device.selectId);
    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.DRY_CONTACT);
  }

  openDryContactGenericConsumerDevice(device: RichDevice): void {
    this.devicesStore.setSelectedDevice(device.selectId);
    this.installationDetailsStore.selectDashboardTypeHelper(
      DashboardTypes.DRY_CONTACT_GENERIC_CONSUMER
    );
  }

  openWallbox(wallboxDevice: WallboxDevicesWithMatchingBoilerSerial): void {
    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.WALLBOX);
    this.devicesStore.setSelectedDevice(wallboxDevice.selectId);
    this.uiService.idOfOpenedWallboxDevice$.next(wallboxDevice.wallboxIndex);
  }

  openHems(): void {
    this.uiService.idOfOpenedWallboxDevice$.next(null);
    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.HEMS);
  }

  openSolarLog(): void {
    this.uiService.idOfOpenedWallboxDevice$.next(null);
    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.SOLARLOG);
  }

  openPvImmersionHeater(device: RichDevice): void {
    this.uiService.idOfOpenedWallboxDevice$.next(null);

    this.devicesStore.selectGatewayOnUserClick(device.gatewaySerial, device.id, device.selectId);

    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.PV_IMMERSION_HEATER);
  }

  selectMainDevice(device: RichDevice): void {
    this.uiService.idOfOpenedWallboxDevice$.next(null);
    this.installationDetailsStore.selectDashboardTypeHelper(DashboardTypes.DEFAULT);

    const isWallboxOfGatewayActive = this.isWallboxDeviceOfSelectedGateway(device);
    const isSolarLog = isSolarLogInverter(device);

    if (isWallboxOfGatewayActive && !isSolarLog) {
      this.devicesStore.setSelectedDevice(device.selectId);

      return;
    }

    if (isSolarLog) {
      this.openSolarLog();
    }

    if (device.isSelected) {
      return;
    }

    const queryParams: Params = { gatewaySerial: device.gatewaySerial };

    this.resetWebsocketConnection();

    this.devicesStore.selectGatewayOnUserClick(device.gatewaySerial, device.id, device.selectId);

    this.router.navigate(['installations/' + this.activatedRoute.snapshot.params['id']], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.uiService.closeActiveOverlay();
    this.dragAndDropService.renderedPanels = {};
    this.cdr.detectChanges();
  }

  mainDevicesTrackBy(_index: number, device: RichDevice): string {
    return device.id;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private resetWebsocketConnection(): void {
    this.liveUpdatesDataService.unsubscribeAll();

    this.installationDetailsStore.gatewaysSerial$.pipe(first()).subscribe((gatewaySerials) => {
      gatewaySerials.forEach((serial) => {
        this.liveUpdatesService.subscribeInstallationsAggregatedStatus(serial);
      });
    });
  }

  private isWallboxDeviceOfSelectedGateway(device: RichDevice): boolean {
    return (
      isWallboxDevice(this.currentlySelectedDevice) &&
      device.gatewaySerial === this.currentlySelectedDevice.gatewaySerial
    );
  }

  private sortAllWallboxDevicesWithMatchingBoilerSerial(): void {
    this.wallboxDevices$ = this.devicesStore.getAllWallboxDevicesWithMatchingBoilerSerial$.pipe(
      switchMap((devices) => {
        const deviceWithBusAddressIndex = devices.map((device) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<DeviceBus, never>(FeatureNames.deviceBus, device.id)
            .pipe(
              map((feature) => ({
                index: Number.isFinite(+feature?.properties?.busAddress?.value)
                  ? +feature?.properties.busAddress.value
                  : 1000, // set as a last ones
                device,
              }))
            )
        );

        return combineLatest(deviceWithBusAddressIndex);
      }),
      map((mappedDevices) =>
        mappedDevices.sort((a, b) => a.index - b.index).map((devices) => devices.device)
      )
    );
  }
}
